import { useEffect, useState } from 'react';
import axios from 'axios';
import MasonryGallery from './MasonryGallery';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SECTIONS = [
  { key: 'live', label: 'Diffusions en direct', icon: '🔴' },
  { key: 'video', label: 'Vidéos', icon: '🎬' },
  { key: 'image', label: 'Photos', icon: '📷' },
];

function fmtDate(d) {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); }
  catch { return d; }
}

function useColumns(mobile) {
  const [columns, setColumns] = useState(mobile ? 1 : 3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setColumns(1);
      else if (w < 1024) setColumns(2);
      else setColumns(3);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  return columns;
}

function MediaCard({ item, onSelect, accentColor }) {
  const isLive = item.media_type === 'live';
  return (
    <div
      onClick={() => onSelect(item)}
      style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', background: isLive ? '#0f172a' : '#e5e5e5' }}
    >
      {isLive ? (
        item.file_url ? (
          <img src={item.file_url} alt={item.caption || 'Diffusion en direct'} style={{ width: '100%', display: 'block', aspectRatio: '16/9', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          </div>
        )
      ) : item.media_type === 'video' ? (
        <video src={item.file_url} style={{ width: '100%', display: 'block' }} muted playsInline />
      ) : (
        <img
          src={item.file_url}
          alt={item.caption || item.activity_name}
          style={{ width: '100%', display: 'block' }}
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/800x600/e5e5e5/525252?text=Photo'; }}
        />
      )}
      {item.is_poster && (
        <span style={{ position: 'absolute', top: 10, left: 10, background: accentColor || '#1e40af', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 6, padding: '3px 8px', letterSpacing: '0.03em' }}>
          📌 AFFICHE
        </span>
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,.55) 0%, rgba(0,0,0,.1) 35%, transparent 60%)' }} />
      {isLive && (
        <span style={{ position: 'absolute', top: 10, right: 10, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 6, padding: '3px 8px', letterSpacing: '0.03em' }}>
          EN DIRECT
        </span>
      )}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '13px 12px 11px' }}>
        <p style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', margin: 0, textShadow: '0 1px 3px rgba(0,0,0,.5)', lineHeight: 1.3 }}>
          {item.caption || item.activity_name || (isLive ? 'Diffusion en direct' : '')}
        </p>
      </div>
    </div>
  );
}

/**
 * Contenu galerie réutilisable (photos/vidéos/live, description, affiches,
 * lien externe/publicité) — utilisé à la fois par la galerie publique de la
 * landing page et par l'onglet Galerie du portail fidèle, pour n'avoir qu'une
 * seule implémentation au lieu de deux versions divergentes.
 */
export default function GalleryContent({ churchId, mobile, accentColor = '#1e40af', emptyText = 'Aucun média disponible pour le moment.' }) {
  const columns = useColumns(mobile);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = churchId ? { church_id: churchId, limit: 200 } : { limit: 200 };
    axios.get(`${API}/activities/gallery/`, { params })
      .then(r => setGallery(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [churchId]);

  if (loading) {
    return <p style={{ color: '#94a3b8', textAlign: 'center', padding: '30px 0' }}>Chargement...</p>;
  }

  if (gallery.length === 0) {
    return <div style={{ textAlign: 'center', padding: '50px 0', color: '#94a3b8', fontSize: '0.88rem' }}>{emptyText}</div>;
  }

  return (
    <div>
      {SECTIONS.map(section => {
        const items = gallery.filter(m => m.media_type === section.key);
        if (items.length === 0) return null;
        return (
          <div key={section.key} style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 17 }}>{section.icon}</span>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>{section.label}</h3>
              <span style={{ fontSize: '0.76rem', color: '#94a3b8', fontWeight: 600 }}>({items.length})</span>
            </div>
            <MasonryGallery
              items={items}
              columns={columns}
              gap={10}
              renderItem={(item) => <MediaCard item={item} onSelect={setSelectedMedia} accentColor={accentColor} />}
            />
          </div>
        );
      })}

      {selectedMedia && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: mobile ? 10 : 24 }}
          onClick={() => setSelectedMedia(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,.4)' }}>
            <div style={{ background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, maxHeight: '50vh' }}>
              {selectedMedia.media_type === 'video' ? (
                <video src={selectedMedia.file_url} controls autoPlay style={{ width: '100%', height: '100%', maxHeight: '50vh', objectFit: 'contain' }} />
              ) : (
                <img src={selectedMedia.file_url} alt={selectedMedia.caption || selectedMedia.activity_name} style={{ width: '100%', height: '100%', maxHeight: '50vh', objectFit: 'contain' }} />
              )}
            </div>
            <div style={{ padding: 20, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>
                  {selectedMedia.caption || selectedMedia.activity_name || 'Sans titre'}
                </h2>
                <button onClick={() => setSelectedMedia(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, flexShrink: 0 }}>✕</button>
              </div>
              {selectedMedia.activity_name && selectedMedia.activity_name !== selectedMedia.caption && (
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: accentColor, marginBottom: 4 }}>
                  {selectedMedia.activity_name}
                </div>
              )}
              {selectedMedia.activity_date && (
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: 8, textTransform: 'capitalize' }}>
                  {fmtDate(selectedMedia.activity_date)}
                </div>
              )}
              {selectedMedia.description && (
                <p style={{ fontSize: '0.85rem', lineHeight: 1.7, color: '#475569', margin: '0 0 16px', whiteSpace: 'pre-line' }}>
                  {selectedMedia.description}
                </p>
              )}
              {(selectedMedia.links?.length > 0 || selectedMedia.external_url) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {selectedMedia.links?.length > 0 ? selectedMedia.links.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: accentColor, color: '#fff', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>
                      {l.label || 'Voir le lien'}
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                    </a>
                  )) : (
                    <a href={selectedMedia.external_url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: accentColor, color: '#fff', borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>
                      Voir le lien
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
